import { useCallback, useMemo, useRef, useState } from "react";

import {
  type Attempt,
  type SessionResult,
  SessionRecorder,
  recordAttempt,
} from "@nav-games/games-core";
import { MNEMONICS, type Mnemonic } from "@nav-games/mnemonics";

import { HelpButton, HelpPanel } from "../../ui/HelpPanel";

interface DrillProps {
  onNavigate: (to: string) => void;
}

const MODULE = "mnemonics";
const GAME = "scenario";
const CARDS_PER_SESSION = 10;
const CHOICES_PER_CARD = 4;

interface Card {
  mnemonic: Mnemonic;
  scenario: string;
  choices: readonly Mnemonic[];
}

function randChoice<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function shuffled<T>(arr: readonly T[]): T[] {
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const a = out[i]!;
    out[i] = out[j]!;
    out[j] = a;
  }
  return out;
}

function buildCard(): Card {
  const mnemonic = randChoice(MNEMONICS);
  const scenario = randChoice(mnemonic.scenarios);
  const distractorPool = MNEMONICS.filter((m) => m.id !== mnemonic.id);
  const distractors = shuffled(distractorPool).slice(0, CHOICES_PER_CARD - 1);
  const choices = shuffled([mnemonic, ...distractors]);
  return { mnemonic, scenario, choices };
}

export function MnemonicsDrill({ onNavigate }: DrillProps) {
  const recorder = useMemo(
    () => new SessionRecorder({ module: MODULE, game: GAME, length: CARDS_PER_SESSION }),
    [],
  );

  const [index, setIndex] = useState(0);
  const [card, setCard] = useState<Card>(() => buildCard());
  const [shownAt, setShownAt] = useState(() => Date.now());
  const [chosen, setChosen] = useState<string | null>(null);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const sessionStartedAt = useRef(Date.now());

  const advance = useCallback(() => {
    if (index + 1 >= CARDS_PER_SESSION) {
      setResult(recorder.summarise());
      return;
    }
    setIndex((i) => i + 1);
    setCard(buildCard());
    setShownAt(Date.now());
    setChosen(null);
  }, [index, recorder]);

  const onChoose = useCallback(
    (id: string) => {
      if (chosen || result) return;
      const ok = id === card.mnemonic.id;
      const attempt: Omit<Attempt, "module"> = {
        char: card.mnemonic.id,
        ok,
        ms: Date.now() - shownAt,
        ts: Date.now(),
      };
      recorder.log(attempt);
      recordAttempt({ ...attempt, module: MODULE });
      setChosen(id);
    },
    [card, chosen, recorder, result, shownAt],
  );

  if (result) {
    return (
      <EndCard
        result={result}
        sessionStartMs={sessionStartedAt.current}
        onAgain={() => {
          setIndex(0);
          setCard(buildCard());
          setShownAt(Date.now());
          setChosen(null);
          setResult(null);
          sessionStartedAt.current = Date.now();
        }}
        onBack={() => onNavigate("/play/reference")}
      />
    );
  }

  const revealed = chosen !== null;
  const m = card.mnemonic;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <HelpButton onOpen={() => setHelpOpen(true)} />
      </div>
      <HelpPanel section={helpOpen ? "mnemonics" : null} onClose={() => setHelpOpen(false)} />

      <p className="small muted">
        Question {index + 1} of {CARDS_PER_SESSION} · pick the right mnemonic for the scenario
      </p>

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Which mnemonic helps?</h3>
        <p style={{ marginBottom: 12, fontSize: 16 }}>{card.scenario}</p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {card.choices.map((c) => {
            const isCorrect = c.id === m.id;
            const isChosen = c.id === chosen;
            return (
              <button
                key={c.id}
                className="secondary"
                disabled={revealed}
                onClick={() => onChoose(c.id)}
                style={{
                  fontSize: 15,
                  padding: "12px 6px",
                  fontWeight: 600,
                  background:
                    revealed && isCorrect
                      ? "rgba(44, 122, 59, 0.18)"
                      : revealed && isChosen
                        ? "rgba(184, 48, 42, 0.18)"
                        : undefined,
                }}
              >
                <div>{c.acronym}</div>
                <div className="small muted" style={{ fontWeight: 500 }}>
                  {c.name}
                </div>
              </button>
            );
          })}
        </div>

        {revealed && (
          <div
            role="status"
            aria-live="polite"
            style={{
              marginTop: 12,
              padding: 12,
              borderRadius: 6,
              background:
                chosen === m.id
                  ? "rgba(44, 122, 59, 0.10)"
                  : "rgba(184, 48, 42, 0.10)",
            }}
          >
            <p style={{ margin: 0, fontWeight: 600 }}>
              {chosen === m.id
                ? `✓ Correct — ${m.acronym}.`
                : `✗ Not quite — this is ${m.acronym}.`}
            </p>
            <p className="small" style={{ margin: "8px 0 0" }}>
              <strong>{m.acronym}:</strong> {m.summary}
            </p>
            {m.steps.length > 0 && (
              <ul
                className="small"
                style={{
                  margin: "6px 0 0",
                  paddingLeft: 16,
                }}
              >
                {m.steps.map((s, i) => (
                  <li key={i} style={{ marginBottom: 2 }}>
                    <strong>{s.key}</strong> — {s.label}
                  </li>
                ))}
              </ul>
            )}
            {m.rule && (
              <pre
                className="small"
                style={{
                  margin: "8px 0 0",
                  padding: 8,
                  background: "var(--bg-elev)",
                  borderRadius: 4,
                  whiteSpace: "pre-wrap",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                }}
              >
                {m.rule}
              </pre>
            )}
            <button onClick={advance} style={{ marginTop: 12, width: "100%" }}>
              {index + 1 >= CARDS_PER_SESSION ? "See score →" : "Next →"}
            </button>
          </div>
        )}
      </div>

      <button className="secondary" onClick={() => onNavigate("/play/reference")}>
        ← Back to Reference
      </button>
    </>
  );
}

function EndCard({
  result,
  sessionStartMs,
  onAgain,
  onBack,
}: {
  result: SessionResult;
  sessionStartMs: number;
  onAgain: () => void;
  onBack: () => void;
}) {
  const elapsedS = Math.round((Date.now() - sessionStartMs) / 1000);
  return (
    <>
      <h2>Session complete</h2>
      <div className="card">
        <p>
          <strong>{result.correct}</strong> / {result.total} correct
          {result.total > 0 && (
            <> ({Math.round((result.correct / result.total) * 100)}%)</>
          )}{" "}
          in {elapsedS}s.
        </p>
      </div>
      <div className="row" style={{ gap: 8, flexDirection: "column", alignItems: "stretch" }}>
        <button onClick={onAgain}>Play another</button>
        <button className="secondary" onClick={onBack}>
          ← Back to Reference
        </button>
      </div>
    </>
  );
}
